import { Typography, Box, Button } from "@mui/material";
import type { BodyPartType, ExamFormTestProm } from "@rmh/defs";
import { useEventListener } from "usehooks-ts";
import type { Dispatch, SetStateAction } from "react";
import { getPromByBodyPart } from "@rmh/utils/src/subjectiveExams";
import { getPromExamQuestions } from "@rmh/utils/src/subjectiveExams/proms";
import { LoadingDots } from "../../common/LoadingDots";
import { useTranslations } from "next-intl";
import PromContent from "./PromContent";

interface PromProps {
  isLoading?: boolean;
  isSaving?: boolean;
  onSelect: (index: number, value: number) => void;
  onNext?: () => void;
  onBack?: (isFirst: boolean) => void;
  onPromCompleted: () => Promise<void>;
  currentIndex: number;
  setCurrentQuestionIndex: Dispatch<SetStateAction<number>>;
  promResponses: (number | undefined)[];
  disableBack?: boolean;
  bodyPart: BodyPartType;
  locale?: string;
}

export const Prom = ({
  isLoading,
  isSaving,
  onSelect,
  currentIndex,
  setCurrentQuestionIndex,
  promResponses = [],
  onNext,
  onBack,
  onPromCompleted,
  disableBack = false,
  bodyPart,
  locale = "en",
}: PromProps) => {
  const t = useTranslations();
  const promKey = getPromByBodyPart(bodyPart);
  const promExamForm = getPromExamQuestions(promKey, locale);

  const examFormTestProm = promExamForm?.examFormTests[0] as ExamFormTestProm;
  const questions = examFormTestProm.questions ?? [];
  const questionCount = questions.length;
  const startIndex = examFormTestProm.startAt ?? 0;
  const isReversedRating = Boolean(examFormTestProm.redToGreen);
  const currentResponse = promResponses[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === questionCount - 1;

  const handleNext = () => {
    if (isLast) {
      void onPromCompleted();
      return;
    }

    setCurrentQuestionIndex((prev) => prev + 1);
    onNext?.();
  };

  const handleBack = () => {
    onBack?.(isFirst);
    setCurrentQuestionIndex((prev) => prev - 1);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (
      event.key === "ArrowRight" &&
      currentResponse &&
      currentIndex !== questionCount - 1
    ) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
    if (event.key === "ArrowLeft" && currentIndex !== 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  useEventListener("keydown", onKeyDown);

  if (isSaving || isLoading) {
    return (
      <Box className="grid h-full w-full place-content-center">
        <LoadingDots />
      </Box>
    );
  }

  return (
    <Box className="flex h-full w-full flex-col items-center overflow-hidden p-8">
      <Typography
        className="flex-none pb-4"
        color="secondary.contrastText"
        variant="bodyL"
      >
        {t("Prom.introText", { questionCount: questions.length })}
      </Typography>
      <PromContent
        index={currentIndex}
        isRatingReversed={isReversedRating}
        onSelect={(value) => {
          onSelect(currentIndex, value);
        }}
        questions={questions}
        responses={promResponses}
        setCurrentQuestionIndex={setCurrentQuestionIndex}
        startIndex={startIndex}
      />
      <Box className="mt-auto flex w-full items-center justify-between">
        <Button disabled={disableBack} onClick={handleBack} variant="outlined">
          {t("Prom.backButton")}
        </Button>
        <Button
          data-cy="prom-next-button"
          disabled={currentResponse === undefined || isLoading}
          onClick={handleNext}
          variant="contained"
        >
          {t("Prom.nextButton")}
        </Button>
      </Box>
    </Box>
  );
};
